import { IntlMessageFormat } from "intl-messageformat";

/**
 * Translations are an external resource: JSON files fetched at runtime, never strings in code.
 * English is the base language; every other locale falls back to it key by key.
 */
export const BASE_LOCALE = "en";
export const SUPPORTED_LOCALES = ["en", "pl", "de"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export type Params = Record<string, string | number | boolean | Date | null | undefined>;
type Messages = Map<string, string>;

export interface TranslatorOptions {
  /** Folder holding `{locale}/{namespace}.json`, e.g. `/i18n`. */
  baseUrl: string;
  namespaces: readonly string[];
  /** Appended as `?v=` so a new build never reads translations cached for the previous one. */
  version?: string;
  fetch?: typeof fetch;
}

export class Translator {
  private readonly messages = new Map<string, Messages>();
  private readonly formats = new Map<string, IntlMessageFormat>();
  private readonly fetcher: typeof fetch;
  private readonly options: TranslatorOptions;

  constructor(options: TranslatorOptions) {
    this.options = options;
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  /** Loads the locale and the base locale; already loaded locales are not fetched again. */
  async load(locale: Locale): Promise<void> {
    await Promise.all([...new Set([BASE_LOCALE, locale])].map((l) => this.loadOne(l)));
  }

  isLoaded(locale: Locale): boolean {
    return this.messages.has(locale);
  }

  /** True when the key exists in the locale or in the base locale. */
  has(locale: Locale, key: string): boolean {
    return this.messages.get(locale)?.has(key) === true || this.messages.get(BASE_LOCALE)?.has(key) === true;
  }

  /** Missing keys render as ⟦key⟧ so they are visible in testing instead of silently empty. */
  t(locale: Locale, key: string, params?: Params): string {
    const own = this.messages.get(locale)?.get(key);
    const message = own ?? this.messages.get(BASE_LOCALE)?.get(key);
    if (message === undefined) {
      return `⟦${key}⟧`;
    }
    const used = own !== undefined ? locale : BASE_LOCALE;
    const cacheKey = `${used}\u0000${key}`;
    let format = this.formats.get(cacheKey);
    if (!format) {
      format = new IntlMessageFormat(message, used);
      this.formats.set(cacheKey, format);
    }
    return String(format.format(params as Record<string, never> | undefined));
  }

  private async loadOne(locale: string): Promise<void> {
    if (this.messages.has(locale)) {
      return;
    }
    const merged: Messages = new Map();
    const files = await Promise.all(
      this.options.namespaces.map(async (ns) => {
        const version = this.options.version ? `?v=${encodeURIComponent(this.options.version)}` : "";
        const response = await this.fetcher(`${this.options.baseUrl}/${locale}/${ns}.json${version}`);
        if (!response.ok) {
          throw new Error(`I18N_LOAD_FAILED ${locale}/${ns} ${response.status}`);
        }
        return (await response.json()) as unknown;
      }),
    );
    for (const file of files) {
      flatten(file, "", merged);
    }
    this.messages.set(locale, merged);
  }
}

/** Picks the first supported locale from the browser's preferences. */
export function negotiateLocale(preferred: readonly string[]): Locale {
  for (const tag of preferred) {
    const language = tag.toLowerCase().split("-")[0];
    const match = SUPPORTED_LOCALES.find((l) => l === language);
    if (match) {
      return match;
    }
  }
  return BASE_LOCALE;
}

function flatten(value: unknown, prefix: string, into: Messages): void {
  if (typeof value === "string") {
    into.set(prefix, value);
    return;
  }
  if (value && typeof value === "object") {
    for (const [key, child] of Object.entries(value)) {
      flatten(child, prefix ? `${prefix}.${key}` : key, into);
    }
  }
}
